/**
 * Vue 3 migration note:
 * Vue 3 removed the global event-emitter API from component instances, so the
 * old pattern `const EventBus = new Vue()` no longer works.
 *
 * This is a tiny drop-in replacement that preserves the exact Vue 2 event-bus
 * surface (`$on` / `$once` / `$off` / `$emit`, including multi-argument emits and
 * array-of-events), so the ~1000 existing call sites across the app keep working
 * without any change. New code should prefer this same instance.
 */

type Handler = (...args: any[]) => void

export class Emitter {
	private handlers: Record<string, Handler[]> = {}

	$on(event: string | string[], cb: Handler): this {
		const events = Array.isArray(event) ? event : [event]
		events.forEach((e) => {
			(this.handlers[e] || (this.handlers[e] = [])).push(cb)
		})
		return this
	}

	$once(event: string, cb: Handler): this {
		const wrapped: Handler = (...args: any[]) => {
			this.$off(event, wrapped)
			cb(...args)
		}
		// keep a reference so $off(event, cb) can also remove a once-handler
		;(wrapped as any).fn = cb
		return this.$on(event, wrapped)
	}

	$off(event?: string | string[], cb?: Handler): this {
		if (!event) {
			this.handlers = {}
			return this
		}
		const events = Array.isArray(event) ? event : [event]
		events.forEach((e) => {
			if (!cb) {
				delete this.handlers[e]
				return
			}
			const cbs = this.handlers[e]
			if (!cbs) return
			this.handlers[e] = cbs.filter(
				(h) => h !== cb && (h as any).fn !== cb
			)
		})
		return this
	}

	$emit(event: string, ...args: any[]): this {
		const cbs = this.handlers[event]
		if (cbs) {
			cbs.slice().forEach((h) => h(...args))
		}
		return this
	}
}

export const ACTIONS = {
	SNACKBAR: 'snackbar',
}

const EventBus = new Emitter()

export default EventBus
